import axios from 'axios'
import { InteractionRequiredAuthError, type AccountInfo, type PublicClientApplication } from '@azure/msal-browser'
import { getAccountForPortal, getApiTokenRequest, getPortalAuthority, msalInstance } from '@config/auth/msalConfig'
import { usePortalModeStore, type PortalMode } from '@stores/usePortalModeStore'
import { getUsernameOverrideForRequest } from '@stores/useStaffOverrideStore'
import { getTeamsSsoToken } from '@config/auth/teamsAuth'
import { useMsalAuthStore } from '@stores/useMsalAuthStore'
import { instrumentPortalClient } from '@services/telemetry/browserTracing'

export function getPortalsApiBaseUrl() {
    const configured = import.meta.env.VITE_PORTALS_API_URL as string | undefined
    if (import.meta.env.DEV && configured?.trim()) {
        return configured.trim().replace(/\/$/, '')
    }

    return `${window.location.origin}/portal-api`
}

async function getMsalToken(instance: PublicClientApplication, hostMode: PortalMode, interactive: boolean, account?: AccountInfo) {
    const teamsToken = await getTeamsSsoToken(!interactive)
    if (teamsToken) return teamsToken

    const acc = account ?? getAccountForPortal(instance.getAllAccounts(), hostMode)
    if (!acc && !interactive) {
        throw new InteractionRequiredAuthError('no_account', 'No signed-in account is available for this portal.')
    }
    const request = {
        ...getApiTokenRequest(hostMode),
        authority: getPortalAuthority(hostMode),
        account: acc ?? undefined,
    }

    try {
        if (!acc) throw new InteractionRequiredAuthError()
        const res = await instance.acquireTokenSilent(request)
        if (!tokenMatchesHostMode(res.accessToken, hostMode)) {
            console.warn('acquireTokenSilent returned a token for a different tenant than the current portal mode', {
                expectedTenantId: getExpectedTenantId(hostMode),
                actualTenantId: getJwtTenantId(res.accessToken),
                hostMode,
            })
        }
        return res.accessToken
    } catch (e) {
        if (!(e instanceof InteractionRequiredAuthError)) {
            console.error('acquireTokenSilent failed', e)
            throw e
        }

        if (!interactive) throw e

        try {
            const res = await instance.acquireTokenPopup({
                ...getApiTokenRequest(hostMode),
                authority: getPortalAuthority(hostMode),
                prompt: 'select_account',
            })
            if (res.account) {
                instance.setActiveAccount(res.account)
            }
            if (!tokenMatchesHostMode(res.accessToken, hostMode)) {
                console.warn('acquireTokenPopup returned a token for a different tenant than the current portal mode', {
                    expectedTenantId: getExpectedTenantId(hostMode),
                    actualTenantId: getJwtTenantId(res.accessToken),
                    hostMode,
                })
            }
            return res.accessToken
        } catch (popupError) {
            console.error('acquireTokenPopup failed', popupError)
            return null
        }
    }
}

function getExpectedTenantId(hostMode: PortalMode) {
    return hostMode === 'parent'
        ? import.meta.env.VITE_AZURE_EXTERNAL_TENANT_ID as string | undefined
        : import.meta.env.VITE_AZURE_TENANT_ID as string | undefined
}

function tokenMatchesHostMode(accessToken: string, hostMode: PortalMode) {
    const expectedTenantId = getExpectedTenantId(hostMode)?.toLowerCase()
    if (!expectedTenantId) return true
    return getJwtTenantId(accessToken)?.toLowerCase() === expectedTenantId
}

function getJwtTenantId(accessToken: string) {
    try {
        const payload = accessToken.split('.')[1]
        if (!payload) return null
        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=')
        const decoded = JSON.parse(atob(normalizedPayload)) as { tid?: string }
        return decoded.tid ?? null
    } catch {
        return null
    }
}

export const portalApi = axios.create({
    baseURL: `${getPortalsApiBaseUrl()}/api`,
})
instrumentPortalClient(portalApi)

export async function getPortalTelemetryHeaders(): Promise<Record<string, string>> {
    const { hostMode, viewMode } = usePortalModeStore.getState()
    const token = await getMsalToken(msalInstance, hostMode, false)
    if (!token) throw new Error('Telemetry requires an authenticated portal session.')
    return { Authorization: `Bearer ${token}`, 'x-host-mode': hostMode, 'x-view-mode': viewMode }
}

export async function getAuthenticatedPortalApiUrl(path: string) {
    const { hostMode } = usePortalModeStore.getState()
    const token = await getMsalToken(msalInstance, hostMode, true)
    if (!token) throw new Error('Unable to authenticate the media request.')

    const url = new URL(`${getPortalsApiBaseUrl()}/api/${path.replace(/^\//, '')}`)
    url.searchParams.set('access_token', token)
    url.searchParams.set('hostMode', hostMode)
    return url.toString()
}

export function getPortalsApiResourceUrl(path: string) {
    if (/^https?:\/\//i.test(path)) {
        const url = new URL(path)
        if (import.meta.env.DEV || url.hostname.toLowerCase() !== 'portalapi.godalming.ac.uk') {
            return path
        }

        return `${getPortalsApiBaseUrl()}${url.pathname}${url.search}${url.hash}`
    }

    const resourcePath = path.startsWith('/') ? path : `/${path}`
    return `${getPortalsApiBaseUrl()}${resourcePath}`
}

portalApi.interceptors.request.use(async (config) => {
    const { hostMode, viewMode } = usePortalModeStore.getState()
    config.withCredentials = false

    ;(config.headers as any).set?.('x-view-mode', viewMode)
    ;(config.headers as any).set?.('x-host-mode', hostMode)

    const method = config.method?.toUpperCase() ?? 'GET'
    let token: string | null
    try {
        token = await getMsalToken(msalInstance, hostMode, method !== 'GET')
    } catch (error) {
        useMsalAuthStore.getState().setGuest()
        throw error
    }
    if (token) {
        if (typeof (config.headers as any).set === 'function') {
            ;(config.headers as any).set('Authorization', `Bearer ${token}`)
        } else {
            ;(config.headers as any).Authorization = `Bearer ${token}`
        }
    } else {
        useMsalAuthStore.getState().setGuest()
        throw new InteractionRequiredAuthError('token_unavailable', 'No access token is available for this request.')
    }

    const override = getUsernameOverrideForRequest(config.url)
    if (override) {
        const existingParams = config.params as Record<string, unknown> | undefined
        if (!existingParams || existingParams.usernameOverride === undefined) {
            config.params = {
                ...(existingParams ?? {}),
                usernameOverride: override,
            }
        }
    }

    return config
})

portalApi.interceptors.response.use(
    (res) => res,
    (error) => Promise.reject(error),
)
